import { SUPPORTED_EXCHANGES } from "../../constants/supported-cex";
import { tokenPairSymbolSchema } from "../../orderbook";
import { ReferralSupportedExchangeIdSchema, SOCIAL_NETWORK } from "../../shared/campaign-types";
import { BATTLE_ROYALE_MIN_PLAYERS } from "../cede-arena/battle-royale";
import {
  ArenaExternalRewardLabelSchema,
  ArenaExternalRewardSlotLabelsSchema,
  ArenaRewardKindSchema,
  assertArenaExternalRewardInvariants,
} from "../cede-arena/external-reward";
import { ArenaCreatorRakeBpsSchema, ArenaRakeBpsSchema } from "../cede-arena/rake";
import {
  isRisingDrawdown,
  isRisingDrawdownIntervalWithinDuration,
  PaperTradingConfigBaseSchema,
  RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
} from "../paper-trading/paper-trading-config";
import { PublicationStatusSchema } from "../solo-cms/publication-status";
import {
  CampaignAutopilotConfigSchema,
  CampaignMarketType,
  CampaignMarketTypeSchema,
  CampaignTradingModeSchema,
  CampaignVisibility,
  TradeToEarnCampaignStatusSchema,
} from "./trade-to-earn-campaign.dto";
import { TradingCampaignRules, TradingRuleSchema } from "./trade-to-earn-rules";
import Decimal from "decimal.js";
import { z } from "zod";

// Re-export CampaignMarketType for backward compatibility
export { CampaignMarketType, CampaignMarketTypeSchema } from "./trade-to-earn-campaign.dto";

export const TradeToEarnCampaignPreviewDtoSchema = z.object({
  id: z.string(),
  title: z.string(),
  name: z.string(),
  tokenSymbol: z.string(),
  status: TradeToEarnCampaignStatusSchema,
  exchangeIds: z.array(z.string()),
  startedAt: z.coerce.date().nullable(),
  endedAt: z.coerce.date().nullable(),
  publishedAt: z.coerce.date().optional(),
  publicationStatus: PublicationStatusSchema,
  spreadThresholdAlert: z.number().optional().nullable(),
  autopilotSpreadThreshold: z.number().optional().nullable(),
  disableHyperliquidRefereeCheck: z.boolean().optional().nullable(),
  saleOwner: z.string().optional().nullable(),
  pairSymbols: z.array(tokenPairSymbolSchema),
  isArenaCompetition: z.boolean().optional().nullable(),
  /** Blueprint that generated this campaign, if any. */
  blueprintId: z.string().optional().nullable(),
});

export type TradeToEarnCampaignPreviewDto = z.infer<typeof TradeToEarnCampaignPreviewDtoSchema>;

const EpochGroupDtoSchema = z.object({
  id: z.uuid().optional(),
  start: z.coerce.date(),
  end: z.coerce.date(),
  rule: TradingRuleSchema,
  epochDurationMs: z.number().nonnegative(),
  status: PublicationStatusSchema,
  isPaused: z.boolean(),
  isPausedFrom: z.coerce.date().optional(),
  exchangeIds: z.array(z.string()), // TODO: replace with EXCHANGES once it's moved to @cede/types
  onlyHubUserWithNoTradeEventsOnExchange: z.boolean().optional().nullable(),
  expectedTradedVolume: z.number().nonnegative(),
  description: z.string(),
  autopilotConfig: CampaignAutopilotConfigSchema.optional().nullable(),
  tradingMode: CampaignTradingModeSchema.optional().nullable(),
  /**
   * Paper-trading fees (bps) for this epoch group; blank and 0 are both
   * fee-free. Only meaningful when the epoch group trades on
   * the paper exchange with a PnL rule. `drawdown` is the optional
   * drawdown-elimination policy for a hand-made arena competition; `battleRoyale`
   * the elimination schedule of a Battle Royale (never both). `priceModel` is how
   * its paper orders fill; fees and price model are locked once the group starts.
   */
  paperTradingConfig: z
    .object(
      PaperTradingConfigBaseSchema.pick({
        makerFeeBps: true,
        takerFeeBps: true,
        drawdown: true,
        battleRoyale: true,
        priceModel: true,
      }).shape,
    )
    .optional()
    .nullable(),
});

const AccessibilityDtoSchema = z.object({
  visibility: z.nativeEnum(CampaignVisibility),
  privateCampaignPartner: z.string(),
  uniqueParticipationByUser: z.boolean(),
  privateCampaignUtms: z.array(z.object({ value: z.string() })),
});

/**
 * Maximum number of pair symbols allowed on a Cede Arena campaign.
 *
 * Not a market-data bound: the engine subscribes one `bbo` stream per DISTINCT pair
 * plus one `allMids` heartbeat per venue, shared across every duel, so the cost is
 * per pair and not per duel-pair — far inside Hyperliquid's ~1000 subscriptions per IP.
 * What grows with this number is per duel: the trade-reporting Cartesian fan-out
 * (`exchangeIds × pairSymbols`), the per-participant per-pair PnL state, and the
 * matching partition set. Load testing sustained 250 markets × 40 duels with no
 * matching-worker leak and Redis flat at ~195 connections; duels, not markets, are
 * the binding axis.
 */
export const ARENA_MAX_PAIR_SYMBOLS = 100;

export const SaveCampaignDtoFieldsSchema = z.object({
  id: z.uuid().optional(),
  name: z.string().min(1, "Name is required"),
  title: z.string().min(1, "Title is required"),
  /**
   * Free-text token symbol shown in branding (page title, OG image, header chip).
   * For non-arena campaigns this is the source of truth and the admin form
   * requires it. Arena campaigns have no single token — they key off
   * `pairSymbols` — so the schema accepts an empty string here and the admin
   * form hides the field.
   */
  tokenSymbol: z.string(),
  /**
   * Market type for the campaign.
   * Used to specify whether trades should be on spot or perpetual markets.
   * Applies to exchanges that support both market types (like Hyperliquid).
   * Defaults to "spot" for backward compatibility.
   */
  marketType: CampaignMarketTypeSchema.optional(),
  epochGroups: z.array(EpochGroupDtoSchema),
  accessibility: AccessibilityDtoSchema,
  rewardVaultAddress: z.string(),
  campaignPartners: z.array(z.object({ value: z.string() })),
  prizePool: z.string(),
  status: PublicationStatusSchema,
  /**
   * Participant whitelist as entered by the admin: wallet address, email, or
   * hubUserId. The API resolves every entry to a hubUserId on save and
   * persists ONLY numbers; unresolvable entries reject the save with a 400.
   */
  authorizedParticipants: z.array(z.union([z.string(), z.number()])),
  dexTokenAddress: z.string().optional(),
  poolAddress: z.string().optional(),
  /**
   * Trading mode for the campaign.
   * Controls which trading methods are available and eligible for rewards.
   * - `manual_only`: Only manual trading widget shown. Autopilot API blocked.
   * - `both`: Both manual and autopilot available. Both eligible for rewards.
   * - `streak_only`: Only autopilot shown. Manual trades processed but NOT eligible for rewards.
   */
  tradingMode: CampaignTradingModeSchema.optional(),
  pairSymbols: z.array(tokenPairSymbolSchema),
  spreadThresholdAlert: z.number().nonnegative().optional().nullable(),
  /**
   * Spread threshold for autopilot protection (percentage).
   * Autopilot cycles are skipped when spread exceeds this threshold.
   * Defaults to 2% if not specified.
   */
  autopilotSpreadThreshold: z.number().nonnegative().optional().nullable(),
  /**
   * When true, the Hyperliquid referee-eligibility check is skipped for this campaign.
   * Used for PnL competitions where revenue is captured via Builder fees / Profit Participation
   * instead of referral fees, so already-referred wallets should not be blocked from joining.
   * Default: false (referee check enforced). Only relevant for Cede Arena campaigns.
   */
  disableHyperliquidRefereeCheck: z.boolean().optional().nullable(),
  /**
   * Discord ID of the campaign owner. Used to ping the owner on Discord when
   * spread-threshold alerts fire. The CMS auto-fills this with the
   * `DEFAULT_SALE_OWNER` (Leandro) but the value is editable.
   */
  saleOwner: z.string().optional(),
  /**
   * Whether cashback is enabled for this campaign.
   * If false, no cashback will be awarded for trades in this campaign.
   * Defaults to true if not specified.
   */
  cashbackEnabled: z.boolean().optional().nullable(),
  /**
   * Override cashback rate for this campaign (0-1, e.g., 0.05 = 5%).
   * If null/undefined, uses the global cashback rate from config.
   */
  cashbackRateOverride: z
    .number()
    .min(0, "Cashback rate must be at least 0")
    .max(1, "Cashback rate must be at most 1 (e.g., 0.25 for 25%)")
    .optional()
    .nullable(),
  /**
   * List of exchange IDs where users must be affiliated (via referral) to participate.
   * If set, only users who registered via Cede referral on these exchanges can join.
   * @see REFERRAL_SUPPORTED_EXCHANGES in shared/campaign-types.ts
   */
  referralOnlyExchangeIds: z.array(ReferralSupportedExchangeIdSchema).optional(),
  /**
   * Social accounts users must connect to be eligible for rewards.
   * If empty, no social accounts are required.
   * Defaults to ["telegram", "x", "discord"] if not specified.
   */
  requiredSocialAccounts: z.array(z.nativeEnum(SOCIAL_NETWORK)).optional(),
  /**
   * Whether this campaign is sponsored by Kraken.
   * When true, Kraken trades for this campaign include a custom User-Agent header
   * so Kraken can attribute the flow to Cede Hub.
   */
  sponsoredByKraken: z.boolean().optional(),
  /**
   * Cede Arena flag. When true, the campaign is shown on `/cede-arena`. When
   * false, it appears in the main `/campaigns` list. Discrete CMS toggle —
   * not derived from the rule type because token-issuer trading-volume
   * campaigns can also use the PnL rule.
   */
  isArenaCompetition: z.boolean().optional(),
  /**
   * Cede Arena per-campaign opt-in. When true, users can opt into this
   * competition directly from the Arena page (no admin matchmaking), locking
   * `entryFees` from their virtual balance. Only meaningful for arena
   * competitions. Defaults to false.
   */
  userCanOptIn: z.boolean().optional(),
  /**
   * Entry fee (decimal USDT, e.g. "10" or "10.5") locked from the user's
   * virtual balance on opt-in and consumed when the competition starts.
   * Required (positive) when `userCanOptIn` is true; null/omitted otherwise.
   * Enforced by the `TradeToEarnCampaign` domain entity on the write path.
   */
  entryFees: z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d{1,6})?$/, {
      error: 'entryFees must be a USDT amount with up to 6 decimals, e.g. "10" or "10.5"',
    })
    .optional()
    .nullable(),
  /**
   * Cede Arena per-campaign opt-in allowlist, entered like
   * `authorizedParticipants`: wallet address, email, or hubUserId. The API
   * resolves every entry to a hubUserId on save and persists ONLY numbers, so a
   * wallet-less (email-OTP) user can be allowlisted too. When non-empty, ONLY
   * these users may directly opt into the competition (server-enforced in the
   * opt-in command); empty/omitted keeps the default — any eligible user can opt
   * in. Only meaningful when `isArenaCompetition` and `userCanOptIn` are both
   * true; ignored otherwise.
   */
  exclusiveOptInParticipants: z.array(z.union([z.string(), z.number()])).optional(),
  /**
   * Cede Arena per-campaign opt-in cap: the maximum number of users that may opt
   * into this competition. The opt-in command rejects further opt-ins once it is
   * reached, and the Arena page locks the opt-in button. Only meaningful when
   * `isArenaCompetition` and `userCanOptIn` are both true; null/omitted means
   * unlimited.
   */
  maxParticipants: z.number().int().positive().optional().nullable(),
  /**
   * Cede Arena guaranteed prize pool. When true, the competition's prize pool at
   * start is set to the configured `prizePool` regardless of participant count —
   * Cede tops up the gap between the pooled entry fees and `prizePool`. When false
   * (default), the pool is self-funded (`participants × entryFees`). Only
   * meaningful when `isArenaCompetition` and `userCanOptIn` are both true.
   */
  cedeFillPrizePool: z.boolean().optional().nullable(),
  /**
   * Cede Arena Spin & Go: when true, the competition's prize pool is resized by a
   * drawn pot multiplier (`potFactor × collected buy-ins`) instead of the flat
   * self-funded pool. Requires a positive `entryFees` and is mutually exclusive
   * with `cedeFillPrizePool` (enforced by the domain entity + a DB CHECK). Only
   * meaningful when `isArenaCompetition` and `userCanOptIn` are both true. The
   * drawn factor itself is server-written at lock, never accepted here.
   */
  isSpinAndGo: z.boolean().optional().nullable(),
  /**
   * Cede Arena platform rake in basis points (0–5000 = 0%–50%, 0.25% steps)
   * taken off this competition's prize pool before the winner is credited.
   * Snapshotted from the config surface at creation and locked for the
   * competition's lifetime. Only meaningful when `isArenaCompetition` is true;
   * null/omitted ⇒ no rake.
   */
  arenaRakeBps: ArenaRakeBpsSchema.optional().nullable(),
  /**
   * Autopilot priority tickets granted, when this paid Arena competition
   * finalizes, to each recipient that played a positive entry fee — bots and
   * waived (whitelisted) entries earn nothing in either mode. Who qualifies is
   * `cede_arena:api:priority_tickets_all_participants_enable`: on ⇒ every
   * entrant; off ⇒ every rewarded rank. Snapshotted from the config surface
   * (blueprint template / buy-in ladder) at creation, like `arenaRakeBps`.
   * 0/omitted ⇒ no tickets.
   */
  autopilotPriorityTickets: z.number().int().min(0).optional(),
  /**
   * Cede Arena creator rake in basis points (0–2000 = 0%–20%), stacked on top of
   * `arenaRakeBps` and paid to the competition creator. Only set for private
   * competitions (the room owner's cut); null elsewhere.
   */
  arenaCreatorRakeBps: ArenaCreatorRakeBpsSchema.optional().nullable(),
  /**
   * The hubUserId the creator rake is paid to (the private-competition room
   * owner). Snapshotted so the payout can credit them; null when there's no
   * creator rake (non-private competitions).
   */
  arenaCreatorHubUserId: z.number().int().positive().optional().nullable(),
  /**
   * `EXTERNAL` ⇒ an off-platform prize (`arenaExternalRewardLabel`) replaces the USDT
   * payout: participants still pay `entryFees`, the pot becomes house revenue, and
   * every rank is credited 0. Omitted ⇒ `CASH`. Arena competitions only.
   */
  arenaRewardKind: ArenaRewardKindSchema.optional().nullable(),
  /**
   * The prize as shown to players, e.g. "1 funded prop-firm account — $10,000".
   * Replaces the prize-pool amount on every surface. Required when
   * `arenaRewardKind` is `EXTERNAL`, and rejected otherwise.
   */
  arenaExternalRewardLabel: ArenaExternalRewardLabelSchema.optional().nullable(),
  /**
   * Optional per-rank prize labels, positional (index `i` labels rank `i + 1`).
   * Omitted ⇒ the headline label applies to 1st place alone and lower ranks show
   * no prize.
   */
  arenaExternalRewardSlotLabels: ArenaExternalRewardSlotLabelsSchema.optional().nullable(),
  /**
   * Multichain reward configuration (additive, optional for now).
   * When set, the campaign distributes rewards in `rewardTokenSymbol`
   * (`rewardTokenAddress`, `rewardTokenDecimals`) on the chain `chainId`.
   * TODO multichain: make required once the CMS form + write path populate them.
   */
  chainId: z.number().optional(),
  rewardTokenAddress: z.string().optional(),
  /** Server-derived on-chain at save time when rewardTokenAddress is set — client values are ignored. */
  rewardTokenSymbol: z.string().optional(),
  /** Server-derived on-chain at save time when rewardTokenAddress is set — client values are ignored. */
  rewardTokenDecimals: z.number().optional(),
  // Cross-field invariants (pairSymbols 1..MAX for arena, tokenSymbol required
  // for non-arena) are enforced by the `TradeToEarnCampaign` domain entity on
  // the write path, so they live in one place rather than being duplicated here.
  //
  // `blueprintId` is intentionally NOT accepted here: blueprint→competition
  // linkage is set only by the Cede Arena blueprint generator (which builds the
  // domain campaign directly), never by the hand-made save payload. Omitting it
  // from the write schema stops a client from forging a blueprint association.
});

/**
 * Cede's prize-pool guarantee needs a pool to guarantee: when `cedeFillPrizePool`
 * is on, `prizePool` must be a positive amount (not missing / `""` / `"0"`).
 * Surfaced at config time (CMS) by the save + blueprint schemas; the domain entity
 * enforces the same invariant at competition start.
 */
export const assertCedeFillPrizePoolHasPositivePool = (
  data: { cedeFillPrizePool?: boolean | null; prizePool?: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (data.cedeFillPrizePool && !(data.prizePool != null && Number(data.prizePool) > 0)) {
    ctx.addIssue({
      code: "custom",
      path: ["prizePool"],
      message: "prizePool must be a positive amount when cedeFillPrizePool is enabled",
    });
  }
};

/**
 * Spin & Go resizes the pool by a drawn multiplier, so it needs a positive entry
 * fee to multiply, and it can't coexist with the fixed cede-fill guarantee. Both
 * are surfaced at config time (CMS) here and re-enforced by the domain entity on
 * the write path. Reused by the blueprint template schema.
 */
export const assertSpinAndGoInvariants = (
  data: { isSpinAndGo?: boolean | null; cedeFillPrizePool?: boolean | null; entryFees?: string | null },
  ctx: z.RefinementCtx,
): void => {
  if (!data.isSpinAndGo) return;
  if (!(data.entryFees != null && new Decimal(data.entryFees).greaterThan(0))) {
    ctx.addIssue({
      code: "custom",
      path: ["entryFees"],
      message: "entryFees must be a positive amount when isSpinAndGo is enabled",
    });
  }
  if (data.cedeFillPrizePool) {
    ctx.addIssue({
      code: "custom",
      path: ["isSpinAndGo"],
      message: "isSpinAndGo and cedeFillPrizePool are mutually exclusive",
    });
  }
};

/**
 * A Spin & Go opt-in (daily) competition scores on a PnL leaderboard, and prepare
 * resizes its pool AFTER claiming the multiplier + consuming fees — a non-PnL epoch
 * group would only fail at prize-write time, stranding the spent multiplier. Require
 * a PnL-rule epoch group at config time; the domain entity re-enforces it on write.
 * Kept separate from {@link assertSpinAndGoInvariants} (which the blueprint template
 * schema reuses) because only the campaign form carries `epochGroups`.
 */
export const assertSpinAndGoOptInHasPnlRule = (
  data: {
    isSpinAndGo?: boolean | null;
    userCanOptIn?: boolean | null;
    epochGroups?: { rule: { rule: TradingCampaignRules } }[];
  },
  ctx: z.RefinementCtx,
): void => {
  if (!data.isSpinAndGo || !data.userCanOptIn) return;
  const hasPnlRuleEpochGroup = (data.epochGroups ?? []).some((group) => group.rule.rule === TradingCampaignRules.PNL);
  if (!hasPnlRuleEpochGroup) {
    ctx.addIssue({
      code: "custom",
      path: ["epochGroups"],
      message: "A Spin & Go opt-in competition must have a PnL-rule epoch group",
    });
  }
};

type IssuePath = (string | number)[];

/**
 * A Battle Royale ranks by elimination order, and its roster freezes from the opt-ins at start:
 * no drawdown, no Spin & Go, no min-volume gate, direct opt-in on, and a cap (when set) of at
 * least 3 players. Shared by the blueprint template and the campaign save, which place the issues.
 */
export const assertBattleRoyaleInvariants = (
  data: {
    drawdown?: unknown;
    isSpinAndGo?: boolean | null;
    userCanOptIn?: boolean | null;
    maxParticipants?: number | null;
    minVolumeUSD?: Decimal;
  },
  paths: Record<"drawdown" | "isSpinAndGo" | "userCanOptIn" | "maxParticipants" | "minVolumeUSD", IssuePath>,
  ctx: z.RefinementCtx,
): void => {
  const reject = (path: IssuePath, message: string) => ctx.addIssue({ code: "custom", path, message });
  if (data.drawdown != null) reject(paths.drawdown, "Battle Royale cannot be combined with drawdown");
  if (data.isSpinAndGo === true) reject(paths.isSpinAndGo, "Battle Royale cannot be a Spin & Go");
  if (data.userCanOptIn !== true) reject(paths.userCanOptIn, "Battle Royale requires direct opt-in");
  if (data.maxParticipants != null && data.maxParticipants < BATTLE_ROYALE_MIN_PLAYERS) {
    reject(paths.maxParticipants, `Battle Royale needs room for at least ${BATTLE_ROYALE_MIN_PLAYERS} players`);
  }
  if (data.minVolumeUSD?.gt(0)) reject(paths.minVolumeUSD, "Battle Royale has no min-volume gate");
};

/**
 * A Battle Royale edited through the campaign form keeps its invariants, and stays a perps arena
 * PnL competition on the paper engine (eliminations run there). Save-only: stored rows hydrate
 * through the field schemas, so the rule must refuse the next save, not turn rows into read errors.
 */
const assertBattleRoyaleEpochGroupsInvariants = (data: SaveCampaignDtoFields, ctx: z.RefinementCtx): void => {
  data.epochGroups.forEach((group, index) => {
    const config = group.paperTradingConfig;
    if (config?.battleRoyale == null) return;
    const groupPath = ["epochGroups", index];
    assertBattleRoyaleInvariants(
      {
        drawdown: config.drawdown,
        isSpinAndGo: data.isSpinAndGo,
        userCanOptIn: data.userCanOptIn,
        maxParticipants: data.maxParticipants,
        minVolumeUSD: group.rule.rule === TradingCampaignRules.PNL ? group.rule.data.minVolumeUSD : undefined,
      },
      {
        drawdown: [...groupPath, "paperTradingConfig", "drawdown"],
        isSpinAndGo: ["isSpinAndGo"],
        userCanOptIn: ["userCanOptIn"],
        maxParticipants: ["maxParticipants"],
        minVolumeUSD: [...groupPath, "rule", "data", "minVolumeUSD"],
      },
      ctx,
    );
    if (data.isArenaCompetition !== true) {
      ctx.addIssue({ code: "custom", path: ["isArenaCompetition"], message: "Battle Royale is an arena competition" });
    }
    if (!group.exchangeIds.every((id) => id === SUPPORTED_EXCHANGES.PAPER)) {
      ctx.addIssue({
        code: "custom",
        path: [...groupPath, "exchangeIds"],
        message: "Battle Royale runs on the paper exchange only",
      });
    }
    if (group.rule.rule !== TradingCampaignRules.PNL) {
      ctx.addIssue({ code: "custom", path: [...groupPath, "rule"], message: "Battle Royale needs a PnL rule" });
    }
    // Eliminations rank through the perps PnL rule; an absent `marketType` is set to perps by the transform.
    if (data.marketType != null && data.marketType !== CampaignMarketType.PERPS) {
      ctx.addIssue({ code: "custom", path: ["marketType"], message: "Battle Royale requires a perps competition" });
    }
  });
};

type SaveCampaignDtoFields = z.infer<typeof SaveCampaignDtoFieldsSchema>;

/**
 * The length of a group's only epoch, or null when the group spans several epochs.
 * A zero epoch duration means the whole group is one epoch; the group's end cuts a longer one.
 */
export const singleEpochDurationMs = (epochDurationMs: number, groupDurationMs: number): number | null =>
  epochDurationMs > 0 && epochDurationMs < groupDurationMs ? null : groupDurationMs;

/**
 * RISING drawdown counts its floor steps from one epoch start, ranks through the
 * perps PnL rule, and latches eliminations per campaign, so it only fits a perps
 * arena campaign with one single-epoch PnL group whose duration holds at least one step.
 */
const assertRisingDrawdownInvariants = (data: SaveCampaignDtoFields, ctx: z.RefinementCtx): void => {
  data.epochGroups.forEach((group, index) => {
    const drawdown = group.paperTradingConfig?.drawdown;
    if (!isRisingDrawdown(drawdown)) return;
    const path = ["epochGroups", index, "paperTradingConfig", "drawdown"];
    // The domain derives the PnL rule's `perps` flag from `marketType` on write.
    if (group.rule.rule !== TradingCampaignRules.PNL || data.marketType !== CampaignMarketType.PERPS) {
      ctx.addIssue({ code: "custom", path, message: "rising drawdown requires a perps PnL rule" });
    }
    // A rising floor is an Arena format: scheduled and private competitions only.
    if (data.isArenaCompetition !== true) {
      ctx.addIssue({ code: "custom", path, message: "rising drawdown requires an arena competition" });
    }
    if (data.epochGroups.length > 1) {
      ctx.addIssue({ code: "custom", path, message: "rising drawdown requires a campaign with a single epoch group" });
    }
    const epochDurationMs = singleEpochDurationMs(group.epochDurationMs, group.end.getTime() - group.start.getTime());
    if (epochDurationMs == null) {
      ctx.addIssue({ code: "custom", path, message: "rising drawdown requires a single-epoch group" });
    } else if (!isRisingDrawdownIntervalWithinDuration(drawdown.stepIntervalSeconds, epochDurationMs / 1000)) {
      ctx.addIssue({
        code: "custom",
        path: [...path, "stepIntervalSeconds"],
        message: RISING_DRAWDOWN_INTERVAL_EXCEEDS_DURATION_MESSAGE,
      });
    }
  });
};

export const SaveCampaignDtoSchema = SaveCampaignDtoFieldsSchema.superRefine((data, ctx) => {
  assertRisingDrawdownInvariants(data, ctx);
  assertCedeFillPrizePoolHasPositivePool(data, ctx);
  assertSpinAndGoInvariants(data, ctx);
  assertSpinAndGoOptInHasPnlRule(data, ctx);
  assertArenaExternalRewardInvariants(data, ctx);
  assertBattleRoyaleEpochGroupsInvariants(data, ctx);
}).transform((data) =>
  data.marketType == null && data.epochGroups.some((group) => group.paperTradingConfig?.battleRoyale != null)
    ? { ...data, marketType: CampaignMarketType.PERPS }
    : data,
);

export type SaveCampaignDto = z.infer<typeof SaveCampaignDtoSchema>;

export const AdminTTECampaignPreviewResponseSchema = z.object({
  campaigns: z.array(TradeToEarnCampaignPreviewDtoSchema),
  page: z.number(),
  totalCount: z.number(),
});
export type AdminTTECampaignPreviewResponse = z.infer<typeof AdminTTECampaignPreviewResponseSchema>;
